/**
 * CORE-846 — общий счётчик участников эфира: мастер + авторизованные +
 * гости-слушатели. Зафиксированное правило владельца.
 *
 * У эфира два непересекающихся множества людей:
 *
 *  - ИМЕНОВАННЫЙ ростер (`namedCount`) — те, кого хаб отдаёт поимённо: на сцене
 *    (мастер + ко-хосты) и авторизованные зрители (`ViewerJoined`). Per-user
 *    presence на бэкенде заводится ТОЛЬКО для авторизованных
 *    (`ChatHub.JoinLiveChat`), поэтому анонимного гостя в этом ростере нет.
 *
 *  - гости-слушатели (`spectatorCount`) — неавторизованные, вошедшие через
 *    `SpectateLive` под стабильным `spectatorId`. Живут отдельной строкой
 *    «Смотрят без аккаунта: N» и в именованный ростер не попадают.
 *
 * Множества не пересекаются (авторизованный ≠ аноним), поэтому их СКЛАДЫВАЕМ.
 *
 * Почему не `viewerCount + spectatorCount`: `viewerCount` — это агрегат ВСЕХ
 * подключений к группе `live-chat-{id}`, и гость там УЖЕ сидит, потому что
 * фронт зовёт `JoinLiveChat` рядом со `SpectateLive` (см. `useLiveChat`).
 * Сложить его со `spectatorCount` значило бы посчитать гостя дважды — ровно
 * прошлый дефект этого тикета. Поэтому база здесь именно ростер, а не агрегат.
 *
 * `spectatorCount` берётся из события хаба как есть: сервер держит ростер
 * гостей в Redis-хэше с ключом по `spectatorId`, так что реконнект того же
 * гостя перезаписывает свою строку и число не растёт. Складываем авторитетное
 * серверное значение, а не накапливаем локально — иначе reconnect раздул бы
 * счётчик.
 */
export function resolveLiveParticipantCount(opts: {
    /** Именованный ростер: мастер + ко-хосты + авторизованные зрители. */
    namedCount: number | null | undefined;
    /** Гости-слушатели без аккаунта (авторитетный счётчик с хаба). */
    spectatorCount: number | null | undefined;
}): number {
    const named = Math.max(0, Math.trunc(opts.namedCount ?? 0) || 0);
    const spectators = Math.max(0, Math.trunc(opts.spectatorCount ?? 0) || 0);
    return named + spectators;
}

/**
 * CORE-1358 (прогон лайва 09.09.2026, решение владельца) — счётчик КОМНАТЫ
 * считает по тому же правилу, что и эфир: ростер плюс слушатели без аккаунта.
 *
 * До этого веб-комната показывала только ростер (ведущий, участники и гости
 * со словом), а слушатели жили отдельной строкой в футере и в счётчик не
 * попадали — «количество слушателей не прибавляется к числу участников».
 *
 * Гость со словом стоит и в ростере, и в гостевом хеше сервера, поэтому в
 * `namedListeners` его нет (см. `spectatorListenerRows`) — иначе он считался
 * бы дважды. `unnamedListeners` — те, кого сервер посчитал, но по имени клиент
 * не знает (`mergeSpectators().unnamedCount`).
 */
export function resolveRoomPeopleCount(opts: {
    /** Ростер комнаты: ведущий, участники, гости со словом. */
    rosterCount: number | null | undefined;
    /** Слушатели, известные по имени и НЕ стоящие в ростере. */
    namedListeners: number | null | undefined;
    /** Слушатели, посчитанные сервером, но не известные по имени. */
    unnamedListeners: number | null | undefined;
}): number {
    return resolveLiveParticipantCount({
        namedCount: opts.rosterCount,
        spectatorCount: (opts.namedListeners ?? 0) + (opts.unnamedListeners ?? 0),
    });
}
